/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import {
  CloudResourceReportStatus,
  DeleteCloudResourceReportInput,
  useDeleteCloudResourceReportMutation,
} from "~/generated/graphql";
import { showError, showSuccess } from "~/services/toast";
import { removeFromApolloCache } from "~/tools";

type Props = {
  reportName: string;
  reportStatus: CloudResourceReportStatus;
};

export function useDeleteCloudResourceReport({
  reportName,
  reportStatus,
}: Props) {
  const [id, setId] = useState("");
  const [deleteCloudResourceReportMutation, { client, loading }] =
    useDeleteCloudResourceReportMutation({
      onCompleted: (response) => {
        if (response.deleteCloudResourceReport) {
          removeFromApolloCache(
            client.cache,
            `CloudResourceReport:${id}`,
            "cloudResourceReports"
          );

          if (reportStatus === CloudResourceReportStatus.InProgress) {
            showSuccess(
              `The generation of the report ${reportName} has been cancelled.`
            );

            return;
          }
          showSuccess(`The report ${reportName} has been deleted.`);
        } else {
          if (reportStatus === CloudResourceReportStatus.InProgress) {
            showError(CANCEL_ERROR_MESSAGE);

            return;
          }
          showError(DELETE_ERROR_MESSAGE);
        }
      },
      onError: (error) => {
        if (reportStatus === CloudResourceReportStatus.InProgress) {
          showError(
            `There was an issue with cancelling the generation of the report: ${error.message}`
          );

          return;
        }
        showError(`Failed to delete the report: ${error.message}`);
      },
    });

  function deleteCloudResourceReport(data: DeleteCloudResourceReportInput) {
    setId(data.id);
    deleteCloudResourceReportMutation({
      variables: {
        input: data,
      },
    });
  }

  return [deleteCloudResourceReport, loading] as const;
}

const CANCEL_ERROR_MESSAGE =
  "There was an issue with cancelling the generation of the report.";
const DELETE_ERROR_MESSAGE = "There was an issue with deleting the report.";
